import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { useTranslation } from '../../i18n';
import { colors, primaryButtonSx, radius } from '../../theme/tokens';

/**
 * Diálogos de feedback.
 *
 * Substitui o SweetAlert2 do legado por um diálogo do MUI com o mesmo
 * comportamento: um modal central com ícone, título, texto e um botão de
 * confirmação; o estado "carregando" bloqueia o fechamento por clique fora.
 *
 * Os textos continuam vindo do i18n — nenhum literal é escrito aqui.
 */

export type FeedbackKind = 'success' | 'error' | 'warning' | 'loading';

export interface FeedbackOptions {
  kind: FeedbackKind;
  title: string;
  text?: string;
  confirmLabel?: string;
  /** Executado ao confirmar (ou ao fechar, quando não há botão). */
  onConfirm?: () => void;
  /** Impede fechar clicando fora — usado nas confirmações de pagamento. */
  blocking?: boolean;
}

interface FeedbackContextValue {
  show: (options: FeedbackOptions) => void;
  close: () => void;
}

const FeedbackContext = createContext<FeedbackContextValue | null>(null);

export function useFeedback(): FeedbackContextValue {
  const context = useContext(FeedbackContext);
  if (!context) {
    throw new Error('useFeedback precisa estar dentro de <FeedbackProvider>');
  }
  return context;
}

const ICONS: Record<Exclude<FeedbackKind, 'loading'>, React.ReactNode> = {
  success: <CheckCircleRoundedIcon sx={{ fontSize: 64, color: colors.success }} />,
  error: <ErrorRoundedIcon sx={{ fontSize: 64, color: colors.error }} />,
  warning: <WarningAmberRoundedIcon sx={{ fontSize: 64, color: colors.warning }} />,
};

export function FeedbackProvider({ children }: { children: React.ReactNode }) {
  const [options, setOptions] = useState<FeedbackOptions | null>(null);
  const { t } = useTranslation();

  const show = useCallback((next: FeedbackOptions) => setOptions(next), []);
  const close = useCallback(() => setOptions(null), []);

  const handleConfirm = useCallback(() => {
    const callback = options?.onConfirm;
    setOptions(null);
    callback?.();
  }, [options]);

  const value = useMemo<FeedbackContextValue>(() => ({ show, close }), [show, close]);

  const isLoading = options?.kind === 'loading';

  return (
    <FeedbackContext.Provider value={value}>
      {children}

      <Dialog
        open={Boolean(options)}
        onClose={isLoading || options?.blocking ? undefined : close}
        maxWidth="xs"
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: radius.lg, px: 1, py: 2 } } }}
      >
        <DialogContent sx={{ textAlign: 'center', pb: 1 }}>
          <Box sx={{ mb: 2 }}>
            {isLoading ? (
              <CircularProgress sx={{ color: colors.primary }} size={56} />
            ) : (
              options && ICONS[options.kind as Exclude<FeedbackKind, 'loading'>]
            )}
          </Box>

          <Typography component="h2" sx={{ fontSize: '1.375rem', fontWeight: 700, mb: 1 }}>
            {options?.title}
          </Typography>

          {options?.text ? (
            <Typography sx={{ color: colors.textSecondary }}>{options.text}</Typography>
          ) : null}
        </DialogContent>

        {!isLoading ? (
          <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
            <Button onClick={handleConfirm} variant="contained" sx={{ ...primaryButtonSx, minWidth: 140 }}>
              {options?.confirmLabel ?? t.common.ok}
            </Button>
          </DialogActions>
        ) : null}
      </Dialog>
    </FeedbackContext.Provider>
  );
}
